import { Injectable } from '@angular/core';
import { CollectionPaths } from '@models/collections';
import { MatterPackageHistoryEntry } from '@models/matter-package';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { DEFAULT_STATE_BASE } from '@state/FirestoreStateBase';
import {
	FirestoreStateBaseForMatter,
	MatterPathParams,
} from '@state/FirestoreStateBaseForMatter';
import * as actions from './matter-packages-history.actions';

export interface MatterPackagesHistoryStateModel extends MatterPathParams {
	currentPackageId: string;
	items: MatterPackageHistoryEntry[];
}
type Ctx = StateContext<MatterPackagesHistoryStateModel>;

const DEFAULT_STATE: MatterPackagesHistoryStateModel = {
	...DEFAULT_STATE_BASE,
	businessId: null,
	matterId: null,
	currentPackageId: null,
	items: [],
};

export interface MatterPackagesHistoryPathParams extends MatterPathParams {
	currentPackageId: string;
}

@State<MatterPackagesHistoryStateModel>({
	name: 'matterPackagesHistory',
	defaults: DEFAULT_STATE,
})
@Injectable({ providedIn: 'root' })
export class MatterPackagesHistoryState extends FirestoreStateBaseForMatter<
	MatterPackageHistoryEntry,
	MatterPackagesHistoryPathParams,
	MatterPackagesHistoryStateModel
> {
	@Selector([MatterPackagesHistoryState])
	static getAllPackageHistoryEntries(
		state: MatterPackagesHistoryStateModel,
	): MatterPackageHistoryEntry[] {
		return state?.items;
	}

	constructor() {
		super(
			(pathParams) =>
				CollectionPaths.business_matter_package_history(
					pathParams.businessId,
					pathParams.matterId,
					pathParams.currentPackageId,
				),
			(payload) => ({ items: payload as MatterPackageHistoryEntry[] }),
			() => DEFAULT_STATE,
		);
	}

	@Action(actions.Create)
	create(ctx: Ctx, action: actions.Create): void {
		this.repo.CreateOrMerge(
			action.payload.entry.timestamp.toString(),
			action.payload.entry,
			{
				businessId: this.businessId,
				matterId: this.matterId,
				currentPackageId: action.payload.packageId,
			},
		);
	}

	@Action(actions.CreateBatch)
	async createBatch(ctx: Ctx, action: actions.CreateBatch): Promise<void> {
		if (!action.payload.entries?.length) return;

		const batch = this.repo.Batch();
		const pathParams = {
			businessId: this.businessId,
			matterId: this.matterId,
			currentPackageId: action.payload.packageId,
		};

		action.payload.entries.forEach((entry) => {
			batch.set(
				this.repo.DocRef(entry.timestamp.toString(), pathParams),
				entry,
			);
		});

		await batch.commit();
	}
}
